import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { EventService } from 'src/app/core/services/event.service';
import { ApiService } from 'src/app/shared/services/api.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-system-config',
  templateUrl: './system-config.component.html',
  styleUrls: ['./system-config.component.scss']
})
export class SystemConfigComponent implements OnInit {

  constructor(
    private apiService: ApiService,
    private modalService: NgbModal,
    public formBuilder: FormBuilder,
    private eventService: EventService,
    private translateService: TranslateService
  ) { }

  listData = <any>[];
  pageSize = 10;
  pageIndex = 1;
  totalCount = 0;
  tableName = 'system-configs';
  params = '';
  searching = false;

  filter = {
    key: ''
  };

  submitted: boolean;
  formData: FormGroup;

  isJsonValue(value: string): boolean {
    if (!value || typeof value !== 'string') {
      return false;
    }
    const trimmed = value.trim();
    if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) {
      return false;
    }
    try {
      JSON.parse(trimmed);
      return true;
    } catch {
      return false;
    }
  }

  formatJsonValue(value: string): string {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  get form() {
    return this.formData.controls;
  }

  ngOnInit() {
    this.fetchData();
  }

  fetchData() {
    this.eventService.setLoading(true);
    this.apiService.getList(this.tableName + `?page=${this.pageIndex}&limit=${this.pageSize}${this.params}`)
      .subscribe({
        next: (res: any) => {
          this.listData = res?.metadata?.results || [];
          this.totalCount = res?.metadata?.pagination?.totalCount || 0;
          this.eventService.setLoading(false);
        },
        error: () => {
          this.apiService.showToastMobile('error', this.translateService.instant('Có lỗi xảy ra khi tải dữ liệu.'));
          this.eventService.setLoading(false);
        }
      });
  }

  submitFilter() {
    this.params = '';
    this.pageIndex = 1;
    this.searching = true;
    if (this.filter.key) {
      this.params += `&key=${encodeURIComponent(this.filter.key)}`;
    }
    this.fetchData();
  }

  clearFilter() {
    this.filter.key = '';
    this.params = '';
    this.pageIndex = 1;
    this.searching = false;
    this.fetchData();
  }

  openModal(content) {
    this.submitted = false;
    this.formData = this.makeForm();
    this.modalService.open(content, { centered: true, scrollable: true, size: 'lg' });
  }

  onEdit(data, content) {
    this.submitted = false;
    this.formData = this.makeForm(data);
    this.modalService.open(content, { centered: true, scrollable: true, size: 'lg' });
  }

  makeForm(d?) {
    let data = d ? d : <any>{};
    return this.formBuilder.group({
      id: [data.id],
      config_key: [data.config_key, [Validators.required, Validators.maxLength(100)]],
      config_value: [data.config_value],
      description: [data.description]
    });
  }

  saveData() {
    this.submitted = true;
    if (this.formData.valid) {
      const value = this.formData.value;
      const payload = {
        config_key: value.config_key,
        config_value: value.config_value,
        description: value.description
      };
      this.eventService.setLoading(true);
      if (value.id) {
        this.apiService.pacth(this.tableName, value.id, payload).subscribe({
          next: () => {
            this.submitted = false;
            this.formData.reset();
            this.fetchData();
            this.modalService.dismissAll();
            this.apiService.showToast(this.translateService.instant('Thành công'), this.translateService.instant('Cập nhật cấu hình thành công'), 'success');
            this.eventService.setLoading(false);
          },
          error: (e) => {
            this.apiService.showToast(this.translateService.instant('Lỗi'), e?.error?.message || this.translateService.instant('Cập nhật cấu hình thất bại'), 'error');
            this.eventService.setLoading(false);
          }
        });
      } else {
        this.apiService.addItem(this.tableName, payload).subscribe({
          next: () => {
            this.fetchData();
            this.apiService.showToast(this.translateService.instant('Thành công'), this.translateService.instant('Thêm mới cấu hình thành công'), 'success');
            this.eventService.setLoading(false);
            this.modalService.dismissAll();
          },
          error: (e) => {
            this.apiService.showToast(this.translateService.instant('Lỗi'), e?.error?.message || this.translateService.instant('Thêm mới cấu hình thất bại'), 'error');
            this.eventService.setLoading(false);
          }
        });
      }
    } else {
      Object.values(this.formData.controls).forEach(control => {
        if (control.status == 'INVALID') {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  onDelete(id) {
    Swal.fire({
      title: this.translateService.instant('Cảnh báo'),
      text: this.translateService.instant('Bạn có chắc chắn muốn xóa cấu hình này?'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#34c38f',
      cancelButtonColor: '#ff3d60',
      confirmButtonText: this.translateService.instant('FORM.WarningConfirm'),
      cancelButtonText: this.translateService.instant('FORM.Cancel')
    }).then(result => {
      if (result.value) {
        this.eventService.setLoading(true);
        this.apiService.deleteItem(this.tableName, id).subscribe({
          next: () => {
            this.eventService.setLoading(false);
            Swal.fire(this.translateService.instant('Thành công'), this.translateService.instant('Đã xóa thành công'), 'success')
              .then(() => this.fetchData());
          },
          error: (e) => {
            this.apiService.showToast(this.translateService.instant('Lỗi'), e?.error?.message || this.translateService.instant('Xóa thất bại'), 'error');
            this.eventService.setLoading(false);
          }
        });
      }
    });
  }
}
